import { useEffect, useState } from 'react';

import { Action } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/types/PendingAction';
import { FlexRow } from '@/components/shared/components/FlexRow';
import { Font } from '@/components/shared/components/Font';
import { Icon } from '@/components/shared/components/Icon';
import { StyledAccordion } from '@/components/shared/components/StyledAccordion';
import { Text } from '@/components/shared/components/Text';
import { useCustomTheme } from '@/hooks/useCustomTheme';

interface NextActionAccordionProps {
  action: Action;
  stepNumber: number;
  disabled: boolean;
  completed: boolean;
  expanded: boolean;
  failed: boolean;
  children: React.ReactNode;
}

export const NextActionAccordion = ({
  action,
  stepNumber,
  disabled,
  completed,
  expanded,
  failed,
  children,
}: NextActionAccordionProps) => {
  const { palette } = useCustomTheme();

  const isDisabled = disabled || completed;

  const [open, setOpen] = useState(expanded);

  useEffect(() => {
    setOpen(expanded);
  }, [expanded, completed, failed]);

  return (
    <StyledAccordion.Accordion
      defaultExpanded={open}
      expanded={open}
      onChange={() => setOpen(!open)}
      disabled={isDisabled}
    >
      <StyledAccordion.AccordionSummary
        expandIcon={
          <Font variant="caption" color="textSecondary" style={{ display: 'flex', opacity: isDisabled ? 0.5 : 1 }}>
            <Icon>expand_more</Icon>
          </Font>
        }
      >
        <FlexRow gridGap={14} style={{ opacity: isDisabled ? 0.5 : 1 }}>
          <FlexRow
            justifyContent={'center'}
            height={'24px'}
            width={'24px'}
            borderRadius={'50%'}
            bgcolor={completed ? palette.success.main : palette.minor.main}
          >
            {completed ? (
              <Icon size={16} color={palette.success.contrastText}>
                check
              </Icon>
            ) : (
              <Font style={{ color: completed ? palette.success.contrastText : palette.minor.contrastText }}>
                {stepNumber}
              </Font>
            )}
          </FlexRow>
          <FlexRow gridGap={6}>
            <Font variant="body2" color="textPrimary">
              <strong>
                <Text tId={action} />
              </strong>
            </Font>
            {failed && (
              <Icon size={20} color={palette.error.main}>
                error
              </Icon>
            )}
          </FlexRow>
        </FlexRow>
      </StyledAccordion.AccordionSummary>
      <StyledAccordion.AccordionDetails style={{ padding: '0px 8px 16px 8px' }}>
        {children}
      </StyledAccordion.AccordionDetails>
    </StyledAccordion.Accordion>
  );
};
